import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../../hooks/useApi';
import SmartImage from './SmartImage';
import { useProgressiveList } from '../../hooks/useProgressiveList';

// Search result card — portrait thumbnail
function ResultCard({ movie, onClick }) {
  const isMovie = (movie.type || '').toLowerCase() !== 'series';
  const year    = movie.releaseyear || movie.year || '';
  const dur     = movie.durationtext || '';
  return (
    <div className="search-card" onClick={onClick}>
      <div className="search-card-thumb">
        <SmartImage movie={movie} type="portrait" alt={movie.title} className="search-card-img" />
        {!isMovie && (
          <div className="search-card-badge">Series</div>
        )}
      </div>
      <div className="search-card-info">
        <p className="search-card-title">{movie.title}</p>
        <p className="search-card-meta">
          {year}{year && dur ? ' · ' : ''}{dur}
        </p>
      </div>
    </div>
  );
}

export default function SearchOverlay({ open, onClose }) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const { results, loading } = useSearch(query);
  const { visibleItems: visibleResults, sentinelRef, hasMore } = useProgressiveList(results, 24);

  // Lock body scroll when overlay is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Auto focus on open / reset on close
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      setQuery('');
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const goTo = useCallback((movie) => {
    onClose();
    navigate(`/show/${movie.filmid || movie.id}`);
  }, [onClose, navigate]);

  if (!open) return null;

  return (
    <div className="search-overlay" onClick={onClose}>
      <div className="search-panel" onClick={e => e.stopPropagation()}>

        {/* Search input — clean bar, no tags */}
        <div className="search-bar">
          <svg className="search-bar-icon" width="18" height="18" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            ref={inputRef}
            className="search-bar-input"
            placeholder="Search movies, series, genres..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
          />
          {query && (
            <button className="search-bar-clear" onClick={() => setQuery('')}>✕</button>
          )}
          <button className="search-bar-close" onClick={onClose}>Close</button>
        </div>

        <div className="search-body">
          {/* Empty state — no tags, no recent, no popular pills */}
          {!query && (
            <div className="search-empty-state">
              <div style={{ textAlign: 'center', padding: '48px 16px', color: 'rgba(255,255,255,0.3)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ marginBottom: 12, opacity: 0.4 }}>
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <p style={{ fontSize: 15, margin: 0 }}>Type to search</p>
              </div>
            </div>
          )}

          {/* Loading */}
          {query && loading && (
            <div className="search-loading">
              <div className="search-spinner" />
              <span>Searching...</span>
            </div>
          )}

          {/* No results */}
          {query && !loading && results.length === 0 && query.length >= 2 && (
            <div className="search-no-results">
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎬</div>
              <p>No results for <strong>"{query}"</strong></p>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                Try different keywords
              </p>
            </div>
          )}

          {/* Results grid */}
          {query && !loading && results.length > 0 && (
            <div className="search-results-section">
              <div className="search-results-count">
                {results.length} result{results.length !== 1 ? 's' : ''} for "<strong>{query}</strong>"
              </div>
              <div className="search-results-grid">
                {visibleResults.map(m => (
                  <ResultCard key={m.filmid || m.id} movie={m}
                    onClick={() => goTo(m)} />
                ))}
              </div>
              {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
